/**
 * 친구 초대 링크(`/invite`)에서 쓰는 순수 상수·로직 모음.
 * `window` / `navigator` 를 직접 참조하지 않는다 — 모든 브라우저 값은 호출부에서 인자로 받는다.
 */

export const INVITE_CODE_PARAM = 'inviteCode';

export const APP_STORE_URL = 'https://apps.apple.com/kr/app/id6783646545';
export const ANDROID_PACKAGE = 'com.smiledragon.viewup';
export const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
export const APP_SCHEME = 'viewup';

// 네이티브(Expo Router)가 아직 이 라우트를 갖고 있지 않다. 앱 쪽 라우트 이름이 정해지면
// 이 상수 한 줄만 바꾸면 되도록 격리해 둔 것이다.
export const APP_INVITE_PATH = 'invite';

export type MobilePlatform = 'ios' | 'android' | 'other';

export const buildAppDeepLink = (inviteCode?: string | null): string => {
  const base = `${APP_SCHEME}://${APP_INVITE_PATH}`;

  if (!inviteCode) return base;

  return `${base}?${INVITE_CODE_PARAM}=${encodeURIComponent(inviteCode)}`;
};

export const buildAndroidIntentUrl = (inviteCode?: string | null): string => {
  const path = inviteCode
    ? `${APP_INVITE_PATH}?${INVITE_CODE_PARAM}=${encodeURIComponent(inviteCode)}`
    : APP_INVITE_PATH;

  // S.browser_fallback_url 덕에 안드로이드는 앱이 설치돼 있지 않으면 크롬이 자동으로 이
  // fallback url(Play 스토어)로 보내준다 — 그래서 iOS와 달리 안드로이드엔 별도 타이머
  // 폴백이 필요 없다.
  return `intent://${path}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`;
};

// iPadOS 13+ Safari 는 기본이 "데스크톱 웹사이트 요청"이라 UA 가 macOS 와 같다.
// UA 만으로는 못 가르므로 터치 포인트로 가른다 (실제 Mac 은 0). 인자를 생략하면(SSR 등)
// 구분할 수 없어 'other' 로 본다.
export const detectMobilePlatform = (userAgent: string, maxTouchPoints = 0): MobilePlatform => {
  const ua = userAgent.toLowerCase();

  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'ios';
  if (ua.includes('android')) return 'android';
  if (ua.includes('macintosh') && maxTouchPoints > 1) return 'ios';

  return 'other';
};

export const getStoreUrl = (platform: MobilePlatform): string | null => {
  if (platform === 'ios') return APP_STORE_URL;
  if (platform === 'android') return PLAY_STORE_URL;

  return null;
};

export interface RequestOriginHeaders {
  forwardedHost?: string | null;
  host?: string | null;
}

// 포트는 허용하되 경로·쿼리·스킴이 섞인 값은 거른다 — og 메타에 그대로 박히는 값이라서다.
const HOST_PATTERN = /^[a-z0-9.-]+(:\d+)?$/i;
const LOCAL_HOST_PATTERN = /^(localhost|127\.0\.0\.1)(:\d+)?$/;

const firstHeaderValue = (value?: string | null): string | undefined =>
  value?.split(',')[0]?.trim() || undefined;

/**
 * 요청이 들어온 도메인의 origin 을 만든다. og:image 처럼 공유 스크래퍼가 읽는 절대 URL 을
 * 환경(dev/prod)마다 자기 도메인으로 맞추기 위함이다. host 를 신뢰할 수 없으면 fallback.
 *
 * 스킴은 x-forwarded-proto 를 믿지 않고 https 로 고정한다 — Next 서버는 이 헤더가 없으면 소켓
 * 기준 `http` 를 직접 채워 넣어서, TLS 를 앞단에서 끊는 배포에선 prod 가 http 로 나간다.
 */
export const resolveRequestOrigin = (headers: RequestOriginHeaders, fallback: string): string => {
  const host = firstHeaderValue(headers.forwardedHost) ?? firstHeaderValue(headers.host);

  if (!host || !HOST_PATTERN.test(host)) return fallback;

  const proto = LOCAL_HOST_PATTERN.test(host) ? 'http' : 'https';

  return `${proto}://${host}`;
};
